/**
 * The EditorForm component
 */
import React, { useEffect, useState } from "react";
import {
  Field,
  reduxForm,
  FieldArray,
  reset as resetReduxForm,
} from "redux-form";
import FieldInput from "../FieldInput";
// import FileInput from '../FileInput'
import ReduxFormButtonGroup from "../ReduxFormButtonGroup";
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import PropTypes from "prop-types";
// import FlashAlert from '../../FlashAlert';
import { useDispatch, useSelector } from "react-redux";
import LineSettings from "./utils/LineSettings";
import AddToCartModal from "../CheckoutCart/utils/AddToCartModal";
import EditorFormSection from "../EditorFormSection";
import TextSvgPreview from "./utils/TextSvgPreview";
import TextSvgPlaceholder from "./utils/TextSvgPlaceholder";
import {
  updateRatio,
  updateScaleFactor,
  updatePreviewSize,
  updateMatrix,
  changeWidth,
  resetSize,
  changeHeight,
} from "../../redux/editor/size";
import { addToCart } from "../../redux/editor/shop";
import {
  addItemToCart,
  resetCartState as resetCart,
} from "../../redux/shop/cart";
import {
  checkMissingChars,
  uploadCustomFont as uploadCustomFontFile,
} from "../../redux/fonts";
import fontSelectors from "../../redux/fonts/selectors";
import { COLORTYPES, COLORS } from "../../utils/colors";
import yourText_mirrored_en from "../../assets/images/yourText-mirrored-en.svg";
import yourText_negative_en from "../../assets/images/yourText-negative-en.svg";
import EditorFormHelpers from "./utils/TextEditorFormHelpers";
import FormattedNumber from "../FormattedNumber";
import instructions from "../../assets/images/Klebeschrift_BxH_de.png";
import EditorDiscountTable from "../EditorDiscountTable";
import icon_lineheight from "../../assets/images/icons/line-height.svg";
import { FaChevronRight, FaTimes } from "react-icons/fa";
import { push } from "connected-react-router";
import { resetCookieError } from "../../redux/shop/errors";
import DataLayerHelper from "../../utils/dataLayer";
import Dropzone from "react-dropzone";
// import editorFileActions from '../../redux/editor/file'
import { show as showFont } from "../../redux/fonts";
import { createProject, resetCreateFail } from "../../redux/cop/projects";
import { REACT_APP_COP_SERVER } from "../../utils/env";

/**
 * Form to handle update and create items
 */
const EditorForm = (props, context) => {
  const {
    currentFormState,
    change,
    disablePreview,
    sku,
    sizeChanged,
    initialValues,
    showAlert,
    previewContent,
    pristine,
    changeComputedTextLength,
  } = props;

  const {
    fonts,
    loadingShow,
    loadedShow,
    uploadingFont,
    uploadedFont,
    missingChars,
    size,
    language,
    addingToCart,
    addedToCart,
    addToShopCartSuccess,
    addToCartProcessing,
    addToCartError,
    addToShopCartProcessing,
    cookieError,
    projectCreateError,
    cart,
  } = useSelector((state) => ({
    fonts: fontSelectors.selectFontsForForm(state.fonts.list),
    projectCreateError: state.projects.createError,
    loadingShow: state.fonts.loadingShow,
    loadedShow: state.fonts.loadedShow,
    uploadingFont: state.fonts.uploadingFont,
    uploadedFont: state.fonts.uploadedFont,
    missingChars: state.fonts.missingChars,
    size: state.editorSize,
    language: state.i18nState.lang,
    addingToCart: state.projects.creating, // This is the new COP API
    addToShopCartProcessing: state.projects.creating,
    addedToCart: state.editorShop.loaded,
    addToShopCartSuccess: state.shopCart.addToShopCartSuccess,
    addToCartProcessing: state.shopCart.addToCartProcessing,
    addToCartError: state.shopCart.error,
    cookieError: state.shopErrors.cookieError,
    cart: state.shopCart.cart,
  }));
  const { materialSerie, lines, baseDimension, calculatedDimension } =
    useSelector((state) => ({
      materialSerie: state.form.editorForm?.values.materialSerie,
      text: state.form.editorForm?.values.text,
      lines: state.form.editorForm?.values.lines,
      fontUpload: state.form.editorForm?.values.fontUpload,
      height: state.form.editorForm?.values.height,
      sizeType: state.form.editorForm?.values.sizeType,
      underglass: state.form.editorForm?.values.underglass,
      negative: state.form.editorForm?.values.negative,
      baseDimension: state.form.editorForm?.values.baseDimension,
      calculatedDimension: state.form.editorForm?.values.calculatedDimension,
    }));

  const dispatch = useDispatch();

  const [open, setOpen] = useState("panel4");
  const [fontUploadModal, setFontUploadModal] = useState(false);
  const [addToCartErrorModal, setAddToCartErrorModal] = useState(false);

  useEffect(() => {
    if (!pristine) DataLayerHelper.addEvent("text_editor_start", "text");
  }, [pristine]);

  const toggleFontUploadModal = () => {
    setFontUploadModal((prevState) => !prevState);
  };

  const toggleAddToCartErrorModal = () => {
    if (addToCartError) {
      dispatch(resetCart());
      dispatch(resetCookieError());
    } else if (projectCreateError) {
      dispatch(resetCreateFail());
    } else {
      setAddToCartErrorModal((prevState) => !prevState);
    }
  };

  const togglePanel = (panelName) => {
    setOpen((state) => (state !== panelName ? panelName : ""));
  };

  const checkLineHeightConstraints = () => {
    const lines =
      (currentFormState.svgJson && currentFormState.svgJson.lines) || [];
    let foundTooSmallLine = false;
    lines.forEach((line) => {
      const fontMinHeight = line.font.min_font_size;
      if (
        line.text !== "" &&
        ((line.computedLineHeight * size.width) / size.previewWidth) *
          size.scaleFactor <=
          fontMinHeight
      ) {
        foundTooSmallLine = true;
      }
    });
    return foundTooSmallLine;
  };

  const checkIsEmptyText = () => {
    const lines =
      (currentFormState.svgJson && currentFormState.svgJson.lines) || [];
    let joined_text = "";
    lines.forEach((line) => {
      joined_text += line.text;
    });
    return joined_text.replace(/(\r\n|\n|\r)/gm, "") === "";
  };

  const handleTextAreaChange = (e) => {
    const textContent = e.target.value;
    const textLines = textContent?.split("\n");

    let lastLine = { ...lines[lines?.length - 1] };
    var newLines = [];
    textLines?.forEach((textLine, idx) => {
      if (lines[idx]) {
        if (lines[idx].font && lines[idx].font.value) {
          dispatch(
            checkMissingChars({
              lineIndex: idx,
              text: textLine,
              line: lines[idx],
              fontTitle: lines[idx].font.value.title,
            })
          );
        }
        newLines.push({ ...lines[idx], text: textLine });
      } else {
        if (lastLine?.font && lastLine?.font?.value) {
          dispatch(
            checkMissingChars({
              lineIndex: idx,
              text: textLine,
              line: lastLine,
              fontTitle: lastLine.font.value.title,
            })
          );
        }
        newLines.push({ ...lastLine, text: textLine });
      }
    });
    change(`lines`, newLines);
  };

  const handleFontChange = (idx, font) => {
    resetFontStyle(idx);
    const { value } = font;
    var loadFontSvg = false;
    value.styles.forEach((style) => {
      if (style.available && !style.loaded) {
        loadFontSvg = true;
      }
    });
    if (loadFontSvg) {
      dispatch(
        showFont({
          id: value.id,
          text: lines[idx].text,
          lineIndex: idx,
          line: lines[idx],
        })
      );
    } else {
      dispatch(
        checkMissingChars({
          text: lines[idx].text,
          lineIndex: idx,
          line: lines[idx],
          fontTitle: value.title,
        })
      );
    }
  };

  const handleDimensionTypeChange = (newType) => {
    const calculatedType = newType === "width" ? "height" : "width";
    if (currentFormState.baseDimension.type !== newType) {
      var calculatedDimension = { ...currentFormState.calculatedDimension };
      var baseDimension = { ...currentFormState.baseDimension };
      change("baseDimension.size", Math.round(calculatedDimension.size));
      change("calculatedDimension.type", calculatedType);
      change("calculatedDimension.size", Math.round(baseDimension.size));
      dispatch(updateMatrix(newType));
    }
  };

  const handleDimensionSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (newSize && newSize > 0) {
      var baseDimension = { ...currentFormState.baseDimension };
      if (baseDimension.type === "width") {
        dispatch(changeWidth(newSize));
        const changedHeight = newSize * size.ratio;
        change("calculatedDimension.size", changedHeight);
      } else {
        dispatch(changeHeight(newSize));
        const changedWidth = newSize / size.ratio;
        change("calculatedDimension.size", changedWidth);
      }
    }
  };

  const handleMaterialTypeChange = (e, newMaterial, oldMaterial) => {
    var firstColorForNewMaterial = COLORS[newMaterial][0];

    var newLines = lines.map((line) => ({
      ...line,
      color: {
        value: firstColorForNewMaterial,
        label: firstColorForNewMaterial.title,
      },
    }));

    change(`lines`, newLines);
    if (newMaterial === "3") {
      // 3 = NEON -> underglass is not available
      change(`underglass`, false);
    }
  };

  const changeDimensionsOnSvgRerender = (width, height) => {
    var baseDimension = { ...currentFormState.baseDimension };
    if (baseDimension.type === "width") {
      change("calculatedDimension.size", height);
    } else {
      change("calculatedDimension.size", width);
    }
  };

  const onFontFileDrop = (acceptedFiles, rejectedFiles) => {
    let newSku = sku;
    // if sku does not exist generate one and use it
    if (!sku) {
      newSku = EditorFormHelpers.generateSKU();
    }
    dispatch(
      uploadCustomFontFile({
        files: acceptedFiles,
        sku: newSku,
        options: { silentUpload: disablePreview, sizeChanged },
      })
    );
  };

  const changeComputedLineSize = (idx, height, width) => {
    change(`lines[${idx}]computedLineHeight`, height);
    change(`lines[${idx}]computedLineWidth`, width);
  };

  const resetFontStyle = (idx) => {
    change(`lines[${idx}]bold`, false);
    change(`lines[${idx}]italic`, false);
  };

  const resetForm = (skipResetCart) => {
    // very custom because form cannot be reinitialized
    change("text", initialValues?.text);
    change("materialSerie", initialValues?.materialSerie);
    change("baseDimension", { type: "width", size: 100 });
    change("calculatedDimension", { type: "height", size: 24 });
    change("lines", initialValues?.lintes);
    change("amount", initialValues?.amount);
    dispatch(resetSize());
    dispatch(resetReduxForm("editorForm"));
    if (!skipResetCart) {
      dispatch(resetCart());
      DataLayerHelper.addEventWithOrigin(
        "behaviour",
        "text editor",
        "resetForm"
      );
    }
  };

  const onSubmit = (values) => {
    let svgJson = currentFormState.svgJson || {};
    const translations = {
      title: context.t("Text"),
      yes: context.t("Yes"),
      no: context.t("No"),
      width: context.t("Width"),
      height: context.t("Height"),
      material: context.t("Material"),
      color: context.t("Color"),
      font: context.t("Font"),
      underglass: context.t("Underglass"),
      lines: context.t("Lines"),
      glossy: context.t("GLOSSY"),
      matt: context.t("MATT"),
      neon: context.t("NEON"),
      frosted: context.t("FROSTED"),
      missingSection: context.t(
        "Missing section: Please contact customer service: kontakt@myfolie.com - "
      ),
    };
    const json = EditorFormHelpers.generateJson(
      values,
      svgJson,
      language,
      translations
    );
    if (
      isNaN(parseInt(json.width)) ||
      isNaN(parseInt(json.height)) ||
      isNaN(json.amount) ||
      parseInt(json.height) === 0 ||
      parseInt(json.width) === 0
    ) {
      // TODO handle here case when number inputs hold text. Just on IE
      console.log("Cant submit with current height, width or amount alues");
      const message = context.t("Height, Width and Amount must be numbers!");
      let alertOptions = {
        message,
        color: "danger",
        origin: "text editor form submit",
        timeout: 5,
      };
      showAlert(alertOptions);
      DataLayerHelper.addEventWithOrigin(
        "error",
        "text editor",
        "onSubmit",
        "values NaN"
      );
    } else if (Object.keys(missingChars)?.length > 0) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "text editor",
        "onSubmit",
        "missing chars"
      );
    } else if (checkLineHeightConstraints()) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "text editor",
        "onSubmit",
        "line too small"
      );
    } else if (checkIsEmptyText()) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "text editor",
        "onSubmit",
        "empty text"
      );
    } else {
      dispatch(
        createProject({
          project: {
            uid: json.sku,
            ...json,
            product_variant_id: 1,
            product_type: "text_plott",
            production_method: "plotting",
            source: "text",
          },
          successCallback: (payload) => {
            dispatch(
              addItemToCart({
                variant_id: 1,
                quantity: json.amount,
                options: {
                  project_uid: json.sku,
                  project_meta: JSON.stringify({
                    pid: payload?.data?.id,
                    title: json.productTitle,
                    description: json.description,
                    width: json.width,
                    height: json.height,
                    thumbnail:
                      REACT_APP_COP_SERVER +
                      payload?.data?.attributes?.proof_preview,
                    uid: json.sku,
                    price: json.price,
                    priceBeforeDiscountAndTax: json.priceBeforeDiscountAndTax,
                    editor: "v3",
                    source: "text",
                  }),
                },
              })
            );
          },
        })
      );
    }
  };

  const renderFormErrors = () => {
    if (Object.keys(missingChars)?.length > 0)
      return context.t(
        "Text with missing characters can't be submitted! Remove the missing characters or change to a font which supports them."
      );
    if (checkLineHeightConstraints())
      return context.t(
        "A Line is too small and, thus not producible, please increase the sizes of the whole foil or the text's font sizes."
      );
    if (checkIsEmptyText())
      return context.t("You added no Text. The Textarea is empty.");
    if (cookieError && addToCartError)
      return (
        context.t("An error occurred while adding your data to the cart. ") +
        context.t(
          "Cookies are disabled in your browser. Please enable mandatory cookies to add an item to your cart."
        )
      );
    if (!cookieError && addToCartError)
      return context.t(
        "An error occurred while adding your data to the cart. Please try again later"
      );
    if (projectCreateError) {
      return (
        <>
          {context.t("We couldn't save your configuration at this time.")}{" "}
          {context.t(
            "This is most likely a temporary issue. Please try again in a few moments. If the problem persists, feel free to contact our support team for assistance. Thank you for your patience!"
          )}
          <br />
          <br />
          <span className="small">
            Error Details:
            <pre>{projectCreateError}</pre>
          </span>
        </>
      );
    }
    return "";
  };

  let svgJson = currentFormState.svgJson || {};
  const calculatedPrice = EditorFormHelpers.calculatePrice(svgJson);

  const selectedMaterials = Object.keys(COLORTYPES).filter(
    (key) => COLORTYPES[key] === parseInt(materialSerie)
  );
  let selectedMaterial = "GLOSSY";
  if (selectedMaterials.length > 0) selectedMaterial = selectedMaterials[0];
  let usedColors = [];

  // FIXME should come from config object
  const discounts = [
    { amount: 2, percentage: 10 },
    { amount: 5, percentage: 15 },
    { amount: 10, percentage: 20 },
    { amount: 20, percentage: 30 },
    { amount: 30, percentage: 40 },
    { amount: 50, percentage: 50 },
    { amount: 100, percentage: 60 },
    { amount: 250, percentage: 70 },
    { amount: 500, percentage: 77 },
  ];

  const orientationOptions = [
    {
      label: context.t("Width"),
      value: "width",
    },
    {
      label: context.t("Height"),
      value: "height",
    },
  ];

  return (
    <Container className="texteditor px-0" fluid>
      <Row className="texteditor__row no-gutters">
        <Col md={{ size: 4 }} className="texteditor__sidebar">
          <div className="p-3 texteditor__sidebar__heading">
            <h1 className="h5 mb-0">
              {context.t("Customize your personal adhesive lettering")}
            </h1>
          </div>
          <form>
            <EditorFormSection
              help={context.t("TextEditor.text.help")}
              panelName={"panel1"}
              panelTitle={context.t("your individual text")}
              open
              keepOpen
              toggle={togglePanel}
            >
              <Row>
                <Col xs={12}>
                  <Field
                    disabled={addingToCart}
                    type="textarea"
                    name="text"
                    placeholder={context.t("Your Text")}
                    onChange={handleTextAreaChange}
                    component={FieldInput}
                    className="texteditor__textarea data-hj-allow"
                    style={{
                      minHeight:
                        lines && lines.length > 4
                          ? `${lines.length * 1.5}rem`
                          : "6rem",
                    }}
                  />
                </Col>
                {/* <Col xs={2}>
              <ul style={{ margin: 0, padding: 0, float: 'right', listStyle: 'none', width: '60px', overflow: 'hidden', fontSize: '12px' }}>
                { svgJson.lines && svgJson.lines.map((ll) => {
                  return (
                    <li style={{ height: '20px', marginTop: '7px', borderBottom: '1px solid #ccc', position: 'relative', paddingLeft: '10px' }}>
                      <i className="fa fa-arrows-v" aria-hidden="true"></i>
                      { Math.round((size.matrix === 'width' ? ll.computedLineHeight / size.productionScaleFactor * size.scaleFactor : ll.computedLineHeight * size.width / size.previewWidth * size.scaleFactor)) }mm
                    </li>
                  );
                })}
              </ul>
              </Col> */}
              </Row>
            </EditorFormSection>
            <EditorFormSection
              help={context.t("TextEditor.dimensions.help")}
              panelName={"panel4"}
              panelTitle={context.t("dimensions of your vinyl")}
              open={open === "panel4"}
              toggle={togglePanel}
            >
              <div className="d-flex align-items-center">
                <div className="flex-fill">
                  <Field
                    disabled={addingToCart}
                    onChange={handleDimensionTypeChange}
                    name="baseDimension.type"
                    id="selectDimention"
                    component={ReduxFormButtonGroup}
                    buttons={orientationOptions}
                    className="mb-3 mr-3 data-hj-allow"
                    size="md"
                  />
                </div>
                <div className="flex-fill">
                  <Field
                    disabled={addingToCart}
                    className="data-hj-allow"
                    type="number"
                    onChange={handleDimensionSizeChange}
                    name="baseDimension.size"
                    component={FieldInput}
                    addonType="append"
                    addonText="mm"
                    min={10}
                  />
                </div>
                <div className="flex-fill text-center pb-3">
                  {baseDimension && (
                    <span>
                      &nbsp;=&nbsp;
                      <FormattedNumber
                        value={Math.round(baseDimension.size * 10) / 100}
                        language={language}
                        unit="cm"
                        decimals={2}
                      />
                    </span>
                  )}
                </div>
              </div>
              <p>
                <strong>
                  {context.t("Calculated")}&nbsp;
                  {calculatedDimension && calculatedDimension.type === "width"
                    ? context.t("Width")
                    : context.t("Height")}
                  :&nbsp;
                </strong>
                {calculatedDimension && (
                  <FormattedNumber
                    value={Math.round(calculatedDimension.size * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                )}
                <span>&nbsp; = </span>
                {calculatedDimension && (
                  <FormattedNumber
                    value={Math.round(calculatedDimension.size * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={2}
                  />
                )}
              </p>

              <p className="semi-small">
                {context.t(
                  "You can either set width or height and we'll calculate the correct dimensions so your vinyl won't be distorted. Please note: All values are Milimeters to give you an exact measurement."
                )}
              </p>
            </EditorFormSection>
            <EditorFormSection
              help={context.t("TextEditor.materialType.help")}
              panelName={"panel2"}
              panelTitle={context.t("type of vinyl")}
              open={open === "panel2"}
              toggle={togglePanel}
            >
              <Field
                onChange={handleMaterialTypeChange}
                disabled={addingToCart}
                id={`materialSerie`}
                type="select"
                name={`materialSerie`}
                component={FieldInput}
              >
                {Object.keys(COLORTYPES).map((prod) => (
                  <option value={COLORTYPES[prod]} key={`product_${prod}`}>
                    {context.t(`${prod}`)}
                    {` (${COLORS[COLORTYPES[prod]].length} ${context.t(
                      "Colors"
                    )})`}
                  </option>
                ))}
              </Field>
            </EditorFormSection>
            <EditorFormSection
              help={context.t("TextEditor.textStyle.help")}
              panelName={"panel3"}
              panelTitle={context.t("customize text style and color")}
              open={open === "panel3"}
              toggle={togglePanel}
            >
              <FieldArray
                disabled={addingToCart}
                name={`lines`}
                component={LineSettings}
                lines={lines}
                fonts={fonts}
                materialSerie={materialSerie}
                handleFontChange={handleFontChange}
              />
              <div className="text-center">
                <div className="small">
                  {context.t(
                    "We've prepared more than 50 fonts for you, but you can also upload your own font. Please clarify the license rights beforehand."
                  )}
                </div>
                <Button color="link" size="sm" onClick={toggleFontUploadModal}>
                  {context.t("Upload your font (.ttf, .otf files)")}
                </Button>
              </div>

              <Modal
                fade={false}
                isOpen={fontUploadModal}
                toggle={toggleFontUploadModal}
              >
                <ModalHeader toggle={toggleFontUploadModal}>
                  {context.t("Upload your font")}
                </ModalHeader>
                <ModalBody>
                  <p>
                    {context.t(
                      "We've prepared more than 50 fonts for you, but you can also upload your own font. Please clarify the license rights beforehand."
                    )}{" "}
                    {context.t(
                      "After a successful upload the font is available in the font selection just for you."
                    )}
                  </p>
                  {uploadingFont && (
                    <em className="text-remark">
                      {context.t("We are uploading your font.")}
                    </em>
                  )}
                  {uploadedFont && (
                    <p className="text-success mb-2">
                      {context.t(
                        "Your font was uploaded successfully. It is now available in the font selection. You can close this window now and continue."
                      )}
                    </p>
                  )}
                  {!uploadingFont && (
                    <Dropzone
                      accept={"font/opentype, .ttf, .otf"}
                      onDrop={onFontFileDrop}
                      rejectClassName={"fileupload-dropzone--reject"}
                      activeClassName="fileupload-dropzone--active"
                    >
                      {({ getRootProps, getInputProps }) => {
                        return (
                          <div
                            {...getRootProps()}
                            className={"fileupload-dropzone"}
                          >
                            <input {...getInputProps()} />
                            <em className="text-remark mr-2">
                              {context.t("Please select a .ttf or .otf file.")}
                            </em>
                            <Button
                              onClick={(e) => e.preventDefault()}
                              size="sm"
                              color="success"
                            >
                              {context.t("choose file")}
                            </Button>
                          </div>
                        );
                      }}
                    </Dropzone>
                  )}
                </ModalBody>
                <ModalFooter>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={toggleFontUploadModal}
                  >
                    {context.t("close")}
                  </Button>
                </ModalFooter>
              </Modal>
            </EditorFormSection>

            <EditorFormSection
              help={context.t("TextEditor.additionalOptions.help")}
              panelName={"panel5"}
              panelTitle={context.t("additional options")}
              open={open === "panel5"}
              toggle={togglePanel}
            >
              <Row>
                <Col xs={6}>
                  <div className="text-center">
                    <img
                      src={yourText_mirrored_en}
                      alt={context.t("reverse cut")}
                      className="img-fluid mb-2 w-100"
                      loading="lazy"
                    />
                    <Field
                      disabled={addingToCart || selectedMaterial === "NEON"}
                      type="switch"
                      name="underglass"
                      id="underglass"
                      component={FieldInput}
                      custom
                      label={context.t("reverse cut")}
                    />
                    <p className="text-left semi-small">
                      {context.t(
                        "We are going to cut your lettering backwards so it can be read normally from outside the window. Some colors may look slightly different depending on your window."
                      )}
                    </p>
                  </div>
                </Col>
                {false && ( //Temporary hide negative plott
                  <Col xs={6}>
                    <div className="text-center">
                      <img
                        src={yourText_negative_en}
                        alt={context.t("cutout plot")}
                        className="img-fluid mb-2 w-100"
                        loading="lazy"
                      />
                      <Field
                        disabled={addingToCart}
                        type="switch"
                        name="negative"
                        id="negative"
                        component={FieldInput}
                        custom
                        label={context.t("cutout plot")}
                      />
                      <p className="text-left semi-small">
                        {context.t(
                          "Your vinyl will be cut normally but we add a 10mm padding additional to the size you've configured and remove the letters from the film."
                        )}
                      </p>
                    </div>
                  </Col>
                )}
              </Row>
            </EditorFormSection>
            <EditorFormSection
              help={context.t("TextEditor.summary.help")}
              panelName={"panel6"}
              panelTitle={context.t("summary")}
              open={open === "panel6"}
              toggle={togglePanel}
            >
              <div className="editorform-resume">
                <div>
                  <span>{context.t("Width")}:&nbsp;</span>
                  {/* {svgJson.width} */}
                  <FormattedNumber
                    value={Math.round(svgJson.width * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                  &nbsp;=&nbsp;
                  <FormattedNumber
                    value={Math.round(svgJson.width * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={2}
                  />
                </div>
                <div>
                  <span>{context.t("Height")}:&nbsp;</span>
                  <FormattedNumber
                    value={Math.round(svgJson.height * 100) / 100}
                    language={language}
                    unit="mm"
                    decimals={1}
                  />
                  &nbsp;=&nbsp;
                  <FormattedNumber
                    value={Math.round(svgJson.height * 10) / 100}
                    language={language}
                    unit="cm"
                    decimals={2}
                  />
                </div>
                {lines && (
                  <div>
                    <span>{context.t("Lines")}:&nbsp;</span>
                    {lines.length}
                  </div>
                )}
                {materialSerie && (
                  <div>
                    <span>{context.t("Product Type")}:&nbsp;</span>
                    {context.t(`${selectedMaterial}`)}
                  </div>
                )}
                <div>
                  <span>{context.t("Number Of Colors Used")}:&nbsp;</span>
                  {usedColors.length}
                </div>
              </div>
            </EditorFormSection>

            {discounts && (
              <EditorDiscountTable discounts={discounts} language={language} />
            )}

            <div className="texteditor__sidebar__submit">
              <div className="d-flex align-items-center justify-content-between">
                <div className="texteditor__sidebar__submit__amount pl-3">
                  <Field
                    disabled={addingToCart}
                    type="number"
                    name="amount"
                    min={1}
                    component={FieldInput}
                  />
                </div>
                <div className="texteditor__sidebar__submit__times text-center">
                  <FaTimes className="fa-fw text-white" />
                </div>
                <div className="texteditor__sidebar__submit__button">
                  <Button
                    color="primary"
                    block
                    type="button"
                    disabled={pristine || addingToCart}
                    onClick={props.handleSubmit(onSubmit)}
                  >
                    <div className="d-flex flex-column flex-lg-row justify-content-between">
                      <strong>
                        <FormattedNumber
                          value={calculatedPrice.finalPrice}
                          language={language}
                          unit="€"
                          decimals={2}
                        />
                      </strong>
                      <strong>
                        {context.t("Add to Cart")}
                        <FaChevronRight className="fa-fw" />
                      </strong>
                    </div>
                  </Button>
                  {(addToShopCartSuccess ||
                    addToCartProcessing ||
                    addToShopCartProcessing) && (
                    <AddToCartModal
                      addItemToCart={(options) =>
                        dispatch(addItemToCart(options))
                      }
                      productCount={cart?.data?.attributes?.item_count}
                      language={language}
                      // FIXME maybe not the best place to do, but okay for production
                      extraProducts={
                        cart?.included?.filter((item) => {
                          return (
                            item?.type === "line_item" &&
                            item?.attributes?.sku === "7004"
                          );
                        }).length > 0
                          ? ["print"]
                          : ["print", "squeegee"]
                      }
                      ctaTitle={context.t(
                        "customize another adhesive lettering"
                      )}
                      addingToCart={addingToCart}
                      navigateTo={(url) => dispatch(push(url))}
                      addedToCart={addedToCart}
                      addToCartError={addToCartError}
                      addToShopCartSuccess={addToShopCartSuccess}
                      addToCartProcessing={
                        addToCartProcessing || addToShopCartProcessing
                      }
                      ctaAction={(skipResetCart) => resetForm(skipResetCart)}
                      resetCart={() => dispatch(resetCart())}
                    />
                  )}

                  <Modal
                    fade={false}
                    isOpen={
                      addToCartErrorModal ||
                      !!addToCartError ||
                      !!projectCreateError
                    }
                    toggle={toggleAddToCartErrorModal}
                    centered
                  >
                    <ModalHeader toggle={toggleAddToCartErrorModal}>
                      {context.t("Alert")}
                    </ModalHeader>
                    <ModalBody>
                      <div>
                        <div className="d-flex justify-content-center mb-3">
                          <FaTimes className="fa-fw fa-5x text-danger" />
                        </div>
                        <p>{renderFormErrors()}</p>
                      </div>
                    </ModalBody>
                    <ModalFooter>
                      <Button
                        color="primary"
                        onClick={toggleAddToCartErrorModal}
                      >
                        {context.t("Close")}
                      </Button>{" "}
                    </ModalFooter>
                  </Modal>
                </div>
              </div>
              <div className="small px-2 bg-light text-center">
                {context.t("price per item")}:{" "}
                <FormattedNumber
                  value={calculatedPrice.finalPrice}
                  language={language}
                  unit="€"
                  decimals={2}
                />
                &#160;-&#160;
                {context.t("incl. VAT")}
                &#160;-&#160;
                <a
                  href={`/${language}/p/versandarten/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {context.t("plus shipping")}
                </a>
              </div>
            </div>
          </form>
        </Col>
        <Col md={{ size: 8 }} className="texteditor__preview p-3">
          <h3 className="text-remark">{context.t("Preview")}</h3>
          {pristine ? (
            <TextSvgPlaceholder size={size} language={language} />
          ) : (
            <TextSvgPreview
              lines={svgJson.lines || []}
              fonts={fonts}
              size={size}
              mirrorSvg={svgJson.underglass}
              text={svgJson.text}
              updateRatio={(args) => {
                dispatch(updateRatio(args));
              }}
              updateScaleFactor={(args) => {
                dispatch(updateScaleFactor(args));
              }}
              updatePreviewSize={(args) => {
                dispatch(updatePreviewSize(args));
              }}
              changeComputedTextLength={changeComputedTextLength}
              changeComputedLineSize={changeComputedLineSize}
              resetFontStyle={resetFontStyle}
              changeDimensionsOnSvgRerender={changeDimensionsOnSvgRerender.bind(
                this
              )}
              loadingFont={loadingShow}
              loadedFont={loadedShow}
              missingChars={missingChars}
              checkLineHeightConstraints={checkLineHeightConstraints}
            />
          )}
          <div className="d-flex bg-white p-3">
            <img
              src={instructions}
              alt="size_instructions"
              height="90px"
              loading="lazy"
            />
            <div>
              <div>{context.t("Size of your vinyl (width x height):")}</div>
              <strong>
                <FormattedNumber
                  value={Math.round(svgJson.width * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={1}
                />
                &nbsp;x&nbsp;
                <FormattedNumber
                  value={Math.round(svgJson.height * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={1}
                />
                &nbsp;=<span> </span>
                <FormattedNumber
                  value={Math.round(svgJson.width * 10) / 100}
                  language={language}
                  unit="cm"
                  decimals={2}
                />
                &nbsp;x&nbsp;
                <FormattedNumber
                  value={Math.round(svgJson.height * 10) / 100}
                  language={language}
                  unit="cm"
                  decimals={2}
                />
              </strong>
              <p className="semi-small">
                {context.t(
                  "Please note the top and bottom of the letters. The dimensions refer to the complete film, not per line."
                )}
              </p>
            </div>
          </div>
          {!pristine && (
            <div className="bg-white px-3 small">
              {context.t(
                "This is the approximate height of the individual line including ascenders and descenders in millimeters:"
              )}
              <Table hover responsive size="sm">
                <thead>
                  <tr>
                    <th>
                      <img
                        width="16"
                        src={icon_lineheight}
                        alt={context.t("lineheight")}
                      />
                    </th>
                    <th colSpan="2">{context.t("line")}</th>
                  </tr>
                </thead>
                <tbody>
                  {svgJson.lines &&
                    svgJson.lines.map((line, index) => {
                      return (
                        <tr key={`lh-idx-${index}`}>
                          <td
                            className={
                              ((line.computedLineHeight * size.width) /
                                size.previewWidth) *
                                size.scaleFactor <
                              line.font.min_font_size
                                ? "text-danger"
                                : "text-secondary"
                            }
                          >
                            <FormattedNumber
                              value={
                                Math.round(
                                  ((line.computedLineHeight * size.width) /
                                    size.previewWidth) *
                                    size.scaleFactor *
                                    100
                                ) / 100
                              }
                              language={language}
                              unit="mm"
                              decimals={1}
                            />
                          </td>
                          <td>#{index + 1}</td>
                          <td>{line.text}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </div>
          )}
          {previewContent}
        </Col>
      </Row>
    </Container>
  );
};

/**  define proptype for the 'translation' function  */
EditorForm.contextTypes = {
  t: PropTypes.func,
};

/** define proptypes for the form */
EditorForm.propTypes = {
  /** function called on submit */
  handleSubmit: PropTypes.func.isRequired,
};

/** export the form as reduxForm component to bind it to the redux store */
// export default reduxForm({  form: 'editorForm' })(EditorForm)

// Decorate with redux-form

export default reduxForm({
  form: "editorForm", // a unique identifier for this form
  // enableReinitialize: true
})(EditorForm);
